import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CogIcon } from '@heroicons/react/24/outline';
import ModernCalendar from '../components/calendar/ModernCalendar';
import CountrySelector from '../components/calendar/CountrySelector';
import HolidayAdminPanel from '../components/calendar/HolidayAdminPanel';
import { useAuth } from '../contexts/AuthContext';

const CalendarPage = () => {
  const { isAdmin, isHR } = useAuth();
  const canManageHolidays = isAdmin || isHR;
  const [selectedCountry, setSelectedCountry] = useState('IN');
  const [showSettings, setShowSettings] = useState(false);
  /** HR/Admin: same screen area — calendar vs holiday management */
  const [mainView, setMainView] = useState('calendar');

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {mainView === 'holidays' && canManageHolidays ? 'Holiday management' : 'Calendar'}
            </h1>
            <p className="text-blue-100">
              {mainView === 'holidays' && canManageHolidays
                ? 'Approve suggested dates, add company holidays, and keep the official calendar accurate.'
                : 'View your schedule, approved company holidays, leave, WFH, and personal Google events.'}
            </p>
            {canManageHolidays ? (
              <div className="mt-4 inline-flex rounded-lg border border-white/30 bg-white/10 p-1">
                <button
                  type="button"
                  onClick={() => setMainView('calendar')}
                  className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    mainView === 'calendar' ? 'bg-white text-blue-700 shadow' : 'text-white hover:bg-white/10'
                  }`}
                >
                  Calendar
                </button>
                <button
                  type="button"
                  onClick={() => setMainView('holidays')}
                  className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    mainView === 'holidays' ? 'bg-white text-blue-700 shadow' : 'text-white hover:bg-white/10'
                  }`}
                >
                  Holiday management
                </button>
              </div>
            ) : null}
          </div>
          {mainView === 'calendar' ? (
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="self-start p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
              title="Holiday region"
              type="button"
            >
              <CogIcon className="w-6 h-6" />
            </button>
          ) : null}
        </div>

        {showSettings && mainView === 'calendar' ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-blue-500 border-opacity-30"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
              <span className="text-blue-100 text-sm font-medium">Holiday region (reference):</span>
              <CountrySelector
                selectedCountry={selectedCountry}
                onCountryChange={setSelectedCountry}
                className="w-full sm:w-[220px]"
              />
            </div>
          </motion.div>
        ) : null}
      </motion.div>

      {canManageHolidays && mainView === 'holidays' ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-gray-200 bg-white shadow-sm"
        >
          <div className="p-5">
            <HolidayAdminPanel />
          </div>
        </motion.div>
      ) : (
        <ModernCalendar selectedCountry={selectedCountry} isHolidayManager={canManageHolidays} />
      )}
    </div>
  );
};

export default CalendarPage;
